import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Check, Clock, Truck, ChefHat, ShoppingBag, ShieldCheck, Store, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { fmtMoney } from '@/lib/realtime';
import { useRealtime, usePolling } from '@/lib/realtime';
import { ORDER_FLOW, ORDER_STATUS_LABELS, ORDER_STATUS_COLORS, getContextualStatusLabel } from '@/lib/orderStatus';
import { PublicHeader, Footer } from '@/components/layout/PublicHeader';
import { FeedbackForm } from '@/components/FeedbackForm';
import { useAuth } from '@/lib/auth';

export default function OrderTracking() {
  const { orderId } = useParams();
  const [sp] = useSearchParams();
  const sessionId = sp.get('session_id');
  const [order, setOrder] = useState(null);
  const [branch, setBranch] = useState(null);
  const [polling, setPolling] = useState(!!sessionId);
  const nav = useNavigate();
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/track/${orderId}`);
      setOrder(data);
      if (data?.branch_id) {
        try {
          const { data: branches } = await api.get('/branches');
          setBranch((branches || []).find((b) => b.id === data.branch_id) || null);
        } catch (e) { /* ignore */ }
      }
    } catch (e) {
      toast.error('Order not found');
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  // NOTE: The auto-prompt "Rate your order" modal has been removed — repeat
  // diners complained that it nagged them every visit. The inline FeedbackForm
  // below stays so customers can still leave a rating on demand for any
  // delivered or completed order.

  // Poll Stripe status after redirect
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let cancelled = false;
    const check = async () => {
      if (cancelled || attempts >= 8) { setPolling(false); load(); return; }
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === 'paid' || data.status === 'expired') {
          setPolling(false);
          load();
          if (data.payment_status === 'paid') toast.success('Payment received!');
          return;
        }
      } catch (e) { /* noop */ }
      setTimeout(check, 2000);
    };
    check();
    return () => { cancelled = true; };
  }, [sessionId, load]);

  // Subscribe to live updates for this order
  useRealtime(
    [`order:${orderId}`],
    useCallback((msg) => {
      if (msg?.order?.id === orderId) setOrder(msg.order);
    }, [orderId]),
  );

  // Safety-net polling
  usePolling(load, 7000, [orderId]);

  if (!order) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <main className="mx-auto max-w-3xl p-6">
          <p className="text-sm text-muted-foreground">Loading order…</p>
        </main>
        <Footer />
      </div>
    );
  }

  const currentIdx = Math.max(0, ORDER_FLOW.indexOf(order.status));
  const progress = order.status === 'cancelled' ? 0 : ((currentIdx + 1) / ORDER_FLOW.length) * 100;

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        {polling && (
          <Card className="mb-4 border-blue-200 bg-blue-50">
            <CardContent className="p-4 text-sm text-blue-900">Confirming your payment…</CardContent>
          </Card>
        )}
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-2">
            <div>
              <CardTitle className="font-display text-xl">Order #{order.order_number}</CardTitle>
              <p className="text-sm text-muted-foreground capitalize">{order.order_type?.replace('_', ' ')}</p>
            </div>
            <Badge data-testid="order-status-badge" className={`border ${ORDER_STATUS_COLORS[order.status] || ''}`}>{getContextualStatusLabel(order.status, order.order_type, order.table_number)}</Badge>
          </CardHeader>
          <CardContent>
            <Progress data-testid="order-progress" value={progress} className="mb-4 h-2" />
            <ol data-testid="order-tracking-timeline" className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              {ORDER_FLOW.map((s, idx) => (
                <li key={s} className={`flex flex-col items-center rounded-md border p-3 text-center text-xs ${idx <= currentIdx ? 'border-primary/30 bg-primary/5 text-primary' : 'border-border bg-card text-muted-foreground'}`}>
                  <span className="mb-1">
                    {idx === 0 && <ShoppingBag className="h-4 w-4" />}
                    {idx === 1 && <ChefHat className="h-4 w-4" />}
                    {idx === 2 && <Check className="h-4 w-4" />}
                    {idx === 3 && <Truck className="h-4 w-4" />}
                    {idx === 4 && <Check className="h-4 w-4" />}
                  </span>
                  {ORDER_STATUS_LABELS[s]}
                </li>
              ))}
            </ol>
            <div data-testid="order-eta-text" className="mt-4 inline-flex items-center gap-2 rounded-md bg-secondary px-3 py-1.5 text-sm">
              <Clock className="h-4 w-4" /> {order.status === 'cancelled' ? 'Cancelled' : 'Live updates enabled — you’ll see status changes here'}
            </div>
          </CardContent>
        </Card>

        {branch && (
          <Card className="mt-4" data-testid="order-branch-card">
            <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Store className="h-5 w-5 text-primary" />
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Prepared by</p>
                  <p data-testid="order-branch-name" className="font-display text-base font-semibold">{branch.name}</p>
                  {branch.address && (
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" /> {branch.address}
                    </p>
                  )}
                </div>
              </div>
              {branch.phone && (
                <a
                  href={`tel:${branch.phone}`}
                  data-testid="order-branch-phone"
                  className="inline-flex items-center gap-2 self-start rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:border-primary hover:text-primary sm:self-auto"
                >
                  <Phone className="h-3.5 w-3.5" /> {branch.phone}
                </a>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="mt-4">
          <CardHeader><CardTitle>Items</CardTitle></CardHeader>
          <CardContent>
            <ul className="divide-y divide-border">
              {(order.items || []).map((it, idx) => (
                <li key={idx} className="flex items-start justify-between gap-2 py-2 text-sm">
                  <span><span className="font-medium">{it.quantity}×</span> {it.name}{it.special_instructions ? <span className="text-muted-foreground"> · {it.special_instructions}</span> : null}</span>
                  <span className="tabular">{fmtMoney(it.line_total)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 grid gap-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="tabular">{fmtMoney(order.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span className="tabular">{fmtMoney(order.tax)}</span></div>
              {order.delivery_fee ? <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span className="tabular">{fmtMoney(order.delivery_fee)}</span></div> : null}
              {order.tip ? <div className="flex justify-between"><span className="text-muted-foreground">Tip</span><span className="tabular">{fmtMoney(order.tip)}</span></div> : null}
              <div className="mt-1 flex justify-between text-base font-semibold"><span>Total</span><span className="tabular">{fmtMoney(order.total)}</span></div>
              <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Payment: {order.payment_method?.toUpperCase()} · {order.payment_status}</span>
                {order.delivery_verification_code && (
                  <span data-testid="order-verification-code" className="rounded bg-secondary px-2 py-0.5 font-mono">Code: {order.delivery_verification_code}</span>
                )}
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button data-testid="order-back-to-menu-button" variant="secondary" onClick={() => nav('/menu')}>Order again</Button>
              <Button data-testid="order-track-refresh-button" variant="ghost" onClick={load}>Refresh</Button>
            </div>
          </CardContent>
        </Card>

        {user && order.customer_id === user.id && ['delivered', 'completed'].includes(order.status) && (
          <div className="mt-4">
            <FeedbackForm orderId={order.id} />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
